import { distanceMeters, type LngLat } from '../../maps/mapboxDrivingRoute';

/**
 * Routing over OpenStreetMap's real indoor walking network (see
 * scripts/generate-yyz-osm-graph.mjs for how the graph is built and why).
 *
 * The important property, which the polygon-derived grid could not provide:
 * these are real corridor CENTRELINES, so a route follows walkways that
 * actually exist instead of inferring where they might be from room shapes.
 * Wall-crossing stops being something to tune down and becomes structurally
 * impossible — there is simply no edge that passes through a wall.
 *
 * LICENCE: the underlying data is ODbL. Any screen showing these routes must
 * visibly credit "(c) OpenStreetMap contributors" — the graph carries the
 * string in `attribution` so it can't drift out of sync with the data.
 */

export type OsmGraphData = {
  attribution: string;
  nodes: [number, number][];
  edges: [number, number][];
  levels: (number[] | null)[];
  gates: { name: string | null; point: [number, number] }[];
  baggageClaims: { name: string | null; point: [number, number] }[];
  landmarks?: { name: string; point: [number, number]; kind: string; level: number | null }[];
  entrances?: { kind: string; point: [number, number]; name: string | null }[];
};

export type OsmLandmark = { name: string; point: LngLat; kind: string; level: number | null };
export type OsmEntrance = { kind: string; point: LngLat; name: string | null };

export type OsmGraph = {
  attribution: string;
  nodes: LngLat[];
  levels: (number[] | null)[];
  adjacency: number[][];
  gates: { name: string | null; point: LngLat }[];
  baggageClaims: { name: string | null; point: LngLat }[];
  landmarks: OsmLandmark[];
  entrances: OsmEntrance[];
};

export function loadOsmGraph(data: OsmGraphData): OsmGraph {
  const adjacency: number[][] = data.nodes.map(() => []);
  for (const [a, b] of data.edges) {
    adjacency[a].push(b);
    adjacency[b].push(a);
  }
  return {
    attribution: data.attribution,
    nodes: data.nodes as LngLat[],
    levels: data.levels,
    adjacency,
    gates: data.gates as { name: string | null; point: LngLat }[],
    baggageClaims: data.baggageClaims as { name: string | null; point: LngLat }[],
    landmarks: (data.landmarks ?? []) as OsmLandmark[],
    entrances: (data.entrances ?? []) as OsmEntrance[],
  };
}

/** Nearest graph node to an arbitrary real-world point, or null if nothing
 * is within `maxMeters`. Snapping is the normal case: a gate, a carousel and
 * a curbside pickup point are all real places that sit a few metres off the
 * walkway centreline rather than exactly on it. */
export function nearestNode(graph: OsmGraph, point: LngLat, maxMeters = 60): number | null {
  let best = -1;
  let bestDist = Infinity;
  for (let i = 0; i < graph.nodes.length; i++) {
    const d = distanceMeters(point, graph.nodes[i]);
    if (d < bestDist) {
      bestDist = d;
      best = i;
    }
  }
  return best >= 0 && bestDist <= maxMeters ? best : null;
}

/** Binary min-heap keyed by f-score. */
class MinHeap {
  private items: number[] = [];
  private priorities: number[] = [];

  get size(): number {
    return this.items.length;
  }

  push(item: number, priority: number): void {
    this.items.push(item);
    this.priorities.push(priority);
    let i = this.items.length - 1;
    while (i > 0) {
      const parent = Math.floor((i - 1) / 2);
      if (this.priorities[parent] <= this.priorities[i]) break;
      this.swap(i, parent);
      i = parent;
    }
  }

  pop(): number | undefined {
    if (this.items.length === 0) return undefined;
    const top = this.items[0];
    const lastItem = this.items.pop()!;
    const lastPriority = this.priorities.pop()!;
    if (this.items.length > 0) {
      this.items[0] = lastItem;
      this.priorities[0] = lastPriority;
      let i = 0;
      for (;;) {
        const left = 2 * i + 1;
        const right = left + 1;
        let smallest = i;
        if (left < this.items.length && this.priorities[left] < this.priorities[smallest]) smallest = left;
        if (right < this.items.length && this.priorities[right] < this.priorities[smallest]) smallest = right;
        if (smallest === i) break;
        this.swap(i, smallest);
        i = smallest;
      }
    }
    return top;
  }

  private swap(a: number, b: number): void {
    const item = this.items[a];
    this.items[a] = this.items[b];
    this.items[b] = item;
    const priority = this.priorities[a];
    this.priorities[a] = this.priorities[b];
    this.priorities[b] = priority;
  }
}

/** A* over the walkway network, by node index. */
export function findPath(graph: OsmGraph, startIndex: number, goalIndex: number): number[] | null {
  const count = graph.nodes.length;
  if (startIndex === goalIndex) return [startIndex];

  const gScore = new Float64Array(count).fill(Infinity);
  const cameFrom = new Int32Array(count).fill(-1);
  const closed = new Uint8Array(count);
  const open = new MinHeap();
  const goal = graph.nodes[goalIndex];

  gScore[startIndex] = 0;
  open.push(startIndex, distanceMeters(graph.nodes[startIndex], goal));

  while (open.size > 0) {
    const current = open.pop()!;
    if (current === goalIndex) {
      const path: number[] = [];
      let node = current;
      while (node !== -1) {
        path.push(node);
        node = cameFrom[node];
      }
      return path.reverse();
    }
    if (closed[current]) continue;
    closed[current] = 1;

    for (const neighbor of graph.adjacency[current]) {
      if (closed[neighbor]) continue;
      const tentative = gScore[current] + distanceMeters(graph.nodes[current], graph.nodes[neighbor]);
      if (tentative < gScore[neighbor]) {
        gScore[neighbor] = tentative;
        cameFrom[neighbor] = current;
        open.push(neighbor, tentative + distanceMeters(graph.nodes[neighbor], goal));
      }
    }
  }
  return null;
}

/**
 * Walking route between two real-world points as an [lng, lat] polyline.
 * Both ends are snapped to the nearest walkway node first. Returns null if
 * either end has no walkway within `maxSnapMeters`, or the two are genuinely
 * unreachable from each other — callers should fall back rather than draw a
 * straight line, which would cut through the building.
 */
export function routeBetween(
  graph: OsmGraph,
  from: LngLat,
  to: LngLat,
  maxSnapMeters = 60,
): LngLat[] | null {
  const start = nearestNode(graph, from, maxSnapMeters);
  const goal = nearestNode(graph, to, maxSnapMeters);
  if (start == null || goal == null) return null;
  const path = findPath(graph, start, goal);
  if (!path) return null;
  return path.map(i => graph.nodes[i]);
}

/**
 * Chains several waypoints into one continuous route (gate -> baggage claim
 * -> pickup). Each leg is routed independently and joined; a leg that can't
 * be routed makes the whole chain fail, so the caller can fall back rather
 * than render a partly-invented path.
 */
export function routeThrough(graph: OsmGraph, waypoints: LngLat[], maxSnapMeters = 60): LngLat[] | null {
  if (waypoints.length < 2) return null;
  const combined: LngLat[] = [];
  for (let i = 1; i < waypoints.length; i++) {
    const leg = routeBetween(graph, waypoints[i - 1], waypoints[i], maxSnapMeters);
    if (!leg) return null;
    combined.push(...(combined.length > 0 ? leg.slice(1) : leg));
  }
  return combined;
}

/** Levels a route passes through, in order of first appearance — used to
 * describe a multi-floor walk ("Level 2 -> 1 -> 0"), not to constrain it. */
export function levelsAlong(graph: OsmGraph, path: LngLat[]): number[] {
  const seen: number[] = [];
  for (const levels of levelsPerPoint(graph, path)) {
    for (const level of levels ?? []) {
      if (!seen.includes(level)) seen.push(level);
    }
  }
  return seen;
}

/** The floor level(s) each point of a route sits on, aligned index-for-index
 * with `path`. `null` means the underlying way carried no `level` tag —
 * common outdoors, where there is no floor to be on.
 *
 * This matters for DRAWING, not routing: Mapbox renders one floor at a time,
 * so a multi-floor route drawn as one flat polyline paints its upstairs
 * portion on top of the downstairs floor plan, which reads exactly like the
 * path cutting through walls. Knowing each point's level lets the caller
 * draw only the part belonging to the floor actually on screen. */
export function levelsPerPoint(graph: OsmGraph, path: LngLat[]): (number[] | null)[] {
  const nodeIndex = new Map<string, number>();
  graph.nodes.forEach((n, i) => nodeIndex.set(`${n[0]},${n[1]}`, i));
  return path.map(point => {
    const idx = nodeIndex.get(`${point[0]},${point[1]}`);
    if (idx == null) return null;
    return graph.levels[idx] ?? null;
  });
}

/**
 * The contiguous stretch of `path` around `currentIndex` that shares a floor
 * with where the walker currently is — i.e. the part of the route that
 * belongs on the floor plan being displayed.
 *
 * Untagged points (outdoors, or ways with no `level`) are treated as
 * matching whatever floor is current, so the outdoor tail of a walk stays
 * attached rather than being clipped off at the building line.
 *
 * Returns index bounds rather than points so callers can slice whatever
 * parallel arrays they hold.
 */
export function sameLevelRange(
  levels: (number[] | null)[],
  currentIndex: number,
): { start: number; end: number } {
  if (levels.length === 0) return { start: 0, end: 0 };
  const clamped = Math.max(0, Math.min(currentIndex, levels.length - 1));
  const current = levels[clamped];
  const shares = (other: number[] | null) => {
    if (current == null || other == null) return true;
    return other.some(l => current.includes(l));
  };
  let start = clamped;
  while (start > 0 && shares(levels[start - 1])) start--;
  let end = clamped;
  while (end < levels.length - 1 && shares(levels[end + 1])) end++;
  return { start, end };
}

const LANDMARK_MAX_DISTANCE_METERS = 25;
const LANDMARK_MIN_SEPARATION_METERS = 18;
const ENTRANCE_MAX_DISTANCE_METERS = 12;
const ENTRANCE_LOOKAHEAD_METERS = 30;

export function landmarksNearPosition(
  graph: OsmGraph,
  position: LngLat,
  limit = 3,
): OsmLandmark[] {
  const scored = graph.landmarks
    .map(landmark => ({ landmark, dist: distanceMeters(landmark.point, position) }))
    .filter(entry => entry.dist <= LANDMARK_MAX_DISTANCE_METERS)
    .sort((a, b) => a.dist - b.dist);

  const picked: OsmLandmark[] = [];
  for (const entry of scored) {
    if (picked.length >= limit) break;
    const tooClose = picked.some(
      p =>
        distanceMeters(p.point, entry.landmark.point) < LANDMARK_MIN_SEPARATION_METERS ||
        p.name === entry.landmark.name,
    );
    if (tooClose) continue;
    picked.push(entry.landmark);
  }
  return picked;
}

export function entranceAhead(
  graph: OsmGraph,
  path: LngLat[],
  fromIndex: number,
): OsmEntrance | null {
  if (path.length === 0 || graph.entrances.length === 0) return null;
  let travelled = 0;
  for (let i = Math.max(0, fromIndex); i < path.length; i++) {
    if (i > Math.max(0, fromIndex)) travelled += distanceMeters(path[i - 1], path[i]);
    if (travelled > ENTRANCE_LOOKAHEAD_METERS) break;
    for (const entrance of graph.entrances) {
      if (distanceMeters(entrance.point, path[i]) <= ENTRANCE_MAX_DISTANCE_METERS) return entrance;
    }
  }
  return null;
}
